import { Box, Checkbox, FormControl, FormControlLabel, FormGroup, Tooltip } from "@mui/material"
import { AddOutlined, HelpOutlined } from "@mui-symbols-material/w300"
import { type FC, useState } from "react"
import { Controller, useFormContext } from "react-hook-form"

import { TextFieldComponent, TooltipLink } from "../Information/TechnicalInformation/TechnicalInformationContent"

import { Button } from "@/components/common/Button"
import { type ProductCreateInput } from "@/graphql/codegen/graphql"
import { CAS_TOOLTIP_URL, UN_NUMBER_TOOLTIP_URL } from "@/screens/Products/constants"

export const ProductRegulatoryContent: FC = () => {
  const { control, watch, setValue } = useFormContext<ProductCreateInput>()
  const casRegistryIds = watch("casRegistryIds") || []
  const [casNumber, setCasNumber] = useState<number>(casRegistryIds?.length || 1)

  const handleCasNumberChange = () => {
    setCasNumber((prev) => prev + 1)
    setValue("casRegistryIds", [...casRegistryIds, ""], { shouldDirty: true })
  }

  const isMaximumCasNumberReached = casNumber >= 10

  return (
    <form className='flex flex-col gap-y-6'>
      <Box className='flex flex-col gap-y-2'>
        <Box className='flex flex-col gap-y-6'>
          {Array.from({ length: casNumber }, (_, i) => (
            <TextFieldComponent
              key={`cas-${i}`}
              control={control}
              name={`casRegistryIds.${i}`}
              label={`CAS - ${i + 1}`}
              placeholder='0000-00-0'
              tooltipText={
                <>
                  Search CAS registry number® (cas rn®)
                  <TooltipLink href={CAS_TOOLTIP_URL} text='HERE' />
                </>
              }
            />
          ))}
        </Box>
        {!isMaximumCasNumberReached && (
          <Button appearance='text' onClick={handleCasNumberChange} startIcon={<AddOutlined />} className='self-end'>
            Additional CAS
          </Button>
        )}

        <TextFieldComponent
          control={control}
          name='unNumber'
          label='UN Number'
          placeholder='0000'
          tooltipText={
            <>
              Determine whether your goods qualify for USMCA
              <TooltipLink href={UN_NUMBER_TOOLTIP_URL} text='HERE' />
            </>
          }
        />
      </Box>
      <FormControl component='fieldset' variant='standard'>
        <FormGroup>
          <Controller
            name='isRohsCompliant'
            control={control}
            render={({ field }) => (
              <Box className='flex items-center gap-x-2'>
                <FormControlLabel
                  control={<Checkbox {...field} checked={watch("isRohsCompliant") ?? false} name='isRohsCompliant' />}
                  label='RoHS Compliant'
                />
                <Tooltip
                  title={
                    <TooltipLink
                      href='https://en.wikipedia.org/wiki/Restriction_of_Hazardous_Substances_Directive'
                      text='RoHS Compliant'
                    />
                  }
                >
                  <HelpOutlined />
                </Tooltip>
              </Box>
            )}
          />
          <Controller
            name='isReachSvhcCompliant'
            control={control}
            render={({ field }) => (
              <Box className='flex items-center gap-x-2'>
                <FormControlLabel
                  control={
                    <Checkbox {...field} checked={watch("isReachSvhcCompliant") ?? false} name='isReachSvhcCompliant' />
                  }
                  label='REACH SVHS Compliant'
                />
                <Tooltip
                  title={
                    <TooltipLink
                      href='https://environment.ec.europa.eu/topics/chemicals/reach-regulation_en'
                      text='REACH SVHS Compliant'
                    />
                  }
                >
                  <HelpOutlined />
                </Tooltip>
              </Box>
            )}
          />
        </FormGroup>
      </FormControl>
    </form>
  )
}
